import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import SuccessToast from "../components/SuccessToast";
import { supabase } from "../supabaseClient";
import { apiUrl } from "../utils/apiBaseUrl";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, applyPagedSelect, totalPagesFromCount } from "../lib/adminPagination";
import {
  adminCard,
  adminDangerBtn,
  adminEmpty,
  adminPageBg,
  adminPageTitle,
  adminPrimaryBtn,
  adminRow,
  adminSearchInput,
  adminSecondaryBtn,
  adminTableHead,
  adminTableWrap,
  adminTd,
  adminTdName,
} from "./adminUi";
import {
  canManageTargetAccount,
  defaultPromoteRole,
  isStaffAccountRole,
  isSuperAdminRole,
  normalizeManagedAccountRole,
} from "../lib/accountAccess";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const emptyPromoteForm = (actorIsSuperAdmin) => ({
  email: "",
  role: defaultPromoteRole(actorIsSuperAdmin),
});

const readStoredAdminRole = () =>
  String(localStorage.getItem("adminRole") || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const formatRole = (value) => {
  if (isSuperAdminRole(value)) return "Super Admin";
  const role = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (role === "staff") return "Staff";
  return "Admin";
};

const isManagedAccountRole = (value) => {
  const role = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return role.includes("admin") || role === "staff";
};

const parseApiError = async (response, fallback) => {
  try {
    const payload = await response.json();
    return payload?.detail || payload?.result || fallback;
  } catch {
    return fallback;
  }
};

const Accounts = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const listTopRef = useRef(null);
  const [search, setSearch] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => isSuperAdminRole(readStoredAdminRole()));
  const [showPromote, setShowPromote] = useState(false);
  const [promoteForm, setPromoteForm] = useState(() => emptyPromoteForm(isSuperAdminRole(readStoredAdminRole())));
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "admin" });
  const [demotingAdmin, setDemotingAdmin] = useState(null);
  const [actionError, setActionError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const showSuccessToast = (message) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(""), 2800);
  };

  const currentUserRef = useRef(null);

  const fetchAdmins = useCallback(async () => {
    try {
      let user = currentUserRef.current;
      if (!user) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        user = session?.user || null;
        currentUserRef.current = user;
      }

      if (user?.id) {
        setCurrentUserId(user.id);
      }

      const fallbackAdmin = user
        ? {
            id: user.id,
            full_name: user.user_metadata?.full_name || user.user_metadata?.name || "Current Admin",
            email: user.email || "-",
            role: localStorage.getItem("adminRole") || "super_admin",
            created_at: user.created_at || null,
          }
        : null;

      let allAdminsQuery = supabase
        .from("users")
        .select("id, full_name, email, role, created_at", { count: "exact" })
        .in("role", ["admin", "staff", "super_admin"])
        .order("created_at", { ascending: false });

      if (search.trim()) {
        const like = `%${search.trim()}%`;
        allAdminsQuery = allAdminsQuery.or(`full_name.ilike.${like},email.ilike.${like}`);
      }
      allAdminsQuery = applyPagedSelect(allAdminsQuery, currentPage, PAGE_SIZE);

      const [currentAdminResult, allAdminsResult] = await Promise.all([
        user?.id
          ? supabase
              .from("users")
              .select("id, full_name, email, role, created_at")
              .eq("id", user.id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        allAdminsQuery,
      ]);

      if (currentAdminResult.data?.role) {
        setIsSuperAdmin(isSuperAdminRole(currentAdminResult.data.role));
      }

      const fallbackRows = currentAdminResult.data ? [currentAdminResult.data] : [];
      const bulkRows = Array.isArray(allAdminsResult.data) ? allAdminsResult.data : [];

      const combined = [...fallbackRows, ...bulkRows]
        .filter(Boolean)
        .filter((userRow) => {
          return isManagedAccountRole(userRow.role);
        })
        .reduce((rows, userRow) => {
          if (!rows.some((existing) => existing.id === userRow.id)) {
            rows.push(userRow);
          }
          return rows;
        }, [])
        .sort((left, right) => {
          const leftDate = new Date(left.created_at || 0).getTime();
          const rightDate = new Date(right.created_at || 0).getTime();
          return rightDate - leftDate;
        });

      if (combined.length > 0) {
        setAdmins(combined);
        setTotalCount(Number(allAdminsResult.count) || combined.length);
      } else if (fallbackAdmin) {
        setAdmins([fallbackAdmin]);
        setTotalCount(1);
      } else {
        setAdmins([]);
        setTotalCount(0);
      }

      if (currentAdminResult.error || allAdminsResult.error) {
        console.warn("Admin accounts query was partially blocked by RLS:", {
          currentAdminError: currentAdminResult.error?.message || null,
          allAdminsError: allAdminsResult.error?.message || null,
        });
      }
    } catch (error) {
      console.error("Error fetching admin accounts:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  useEffect(() => {
    fetchAdmins();

    const subscription = supabase
      .channel("admin-accounts")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "users" },
        () => {
          fetchAdmins();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchAdmins]);

  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedAdmins = admins;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const closePromote = () => {
    setShowPromote(false);
    setPromoteForm(emptyPromoteForm(isSuperAdmin));
    setActionError("");
  };

  const closeEdit = () => {
    setEditingAdmin(null);
    setEditForm({ name: "", role: "admin" });
    setActionError("");
  };

  const closeDemote = () => {
    setDemotingAdmin(null);
    setActionError("");
  };

  const handlePromote = async () => {
    const email = promoteForm.email.trim().toLowerCase();
    if (!email) {
      setActionError("Email is required.");
      return;
    }

    setSaving(true);
    setActionError("");
    try {
      const response = await fetch(apiUrl("/admin/accounts/promote/"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          role: isSuperAdmin ? promoteForm.role : "staff",
        }),
      });

      if (!response.ok) {
        setActionError(await parseApiError(response, "Unable to promote this account."));
        return;
      }

      closePromote();
      await fetchAdmins();
      showSuccessToast("Access granted.");
    } catch (error) {
      setActionError(error?.message || "Unable to reach the accounts API.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editingAdmin?.id) return;
    const name = editForm.name.trim();
    if (!name) {
      setActionError("Name is required.");
      return;
    }

    setSaving(true);
    setActionError("");
    try {
      const response = await fetch(apiUrl(`/admin/accounts/${editingAdmin.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          full_name: name,
          role: isSuperAdmin ? editForm.role : "staff",
        }),
      });

      if (!response.ok) {
        setActionError(await parseApiError(response, "Unable to update this account."));
        return;
      }

      closeEdit();
      await fetchAdmins();
      showSuccessToast(isStaffAccountRole(editForm.role) ? "Staff account updated." : "Admin account updated.");
    } catch (error) {
      setActionError(error?.message || "Unable to reach the accounts API.");
    } finally {
      setSaving(false);
    }
  };

  const handleDemote = async () => {
    if (!demotingAdmin?.id) return;

    setSaving(true);
    setActionError("");
    try {
      const response = await fetch(apiUrl(`/admin/accounts/${demotingAdmin.id}/demote/`), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        setActionError(await parseApiError(response, "Unable to remove access."));
        return;
      }

      closeDemote();
      await fetchAdmins();
      showSuccessToast(
        isStaffAccountRole(demotingAdmin.role) ? "Staff access revoked." : "Admin access revoked."
      );
    } catch (error) {
      setActionError(error?.message || "Unable to reach the accounts API.");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (admin) => {
    setActionError("");
    setEditingAdmin(admin);
    setEditForm({
      name: admin.full_name || "",
      role: normalizeManagedAccountRole(admin.role),
    });
  };

  const columnCount = 4;

  return (
    <div className={adminPageBg}>
      <AdminTopBar />
      <SuccessToast message={toastMessage} />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
          <div className="min-w-0">
            <h1 className={adminPageTitle}>
              Admin Accounts and Roles
            </h1>
            <p className="mt-1 text-sm text-gray-500 font-medium">
              {isSuperAdmin
                ? "Promote existing users and manage administrator access levels."
                : "Promote existing users to scanner staff and revoke staff access."}
            </p>
          </div>

          <button
            onClick={() => {
              setActionError("");
              setPromoteForm(emptyPromoteForm(isSuperAdmin));
              setShowPromote(true);
            }}
            className={`${adminPrimaryBtn} w-full sm:w-auto shrink-0`}
          >
            <Plus size={16} />
            {isSuperAdmin ? "Add Admin" : "Add Staff"}
          </button>
        </div>

        <div className={`max-w-7xl mx-auto ${adminCard}`} ref={listTopRef}>
          <div className="mb-4 relative max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={adminSearchInput}
              placeholder="Search name or email..."
            />
          </div>

          <BusyTable show={showSkeleton} tone="admin">
          <div className={adminTableWrap}>
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className={adminTableHead}>
                  <th className="px-4 py-3 text-left">Account Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-center">Role</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {paginatedAdmins.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount} className={adminEmpty}>
                      {loading
                        ? "Loading..."
                        : search
                        ? "No admin accounts match that search."
                        : isSuperAdmin
                          ? "No admin accounts yet. Superadmin can promote existing users here."
                          : "No staff accounts yet. Promote an existing user to staff here."}
                    </td>
                  </tr>
                ) : (
                  paginatedAdmins.map((admin) => {
                    const isCurrentUser = Boolean(currentUserId && admin.id === currentUserId);
                    const canManageRow = canManageTargetAccount(isSuperAdmin, admin.role);
                    return (
                      <tr key={admin.id} className={adminRow}>
                        <td className={adminTdName}>
                          {admin.full_name || "Unknown"}
                        </td>
                        <td className={adminTd}>
                          {admin.email || "-"}
                        </td>
                        <td className="px-4 py-4 text-center text-sm">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isSuperAdminRole(admin.role)
                                ? "bg-gray-100 text-gray-900"
                                : isStaffAccountRole(admin.role)
                                  ? "bg-slate-100 text-slate-700"
                                  : "bg-red-50 text-brand-maroon"
                            }`}
                          >
                            {formatRole(admin.role)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {canManageRow ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => openEdit(admin)}
                                className={adminSecondaryBtn}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  setActionError("");
                                  setDemotingAdmin(admin);
                                }}
                                disabled={isCurrentUser}
                                title={
                                  isCurrentUser
                                    ? "You cannot remove your own admin access."
                                    : isStaffAccountRole(admin.role)
                                      ? "Revoke staff access"
                                      : "Revoke admin access"
                                }
                                className={`${adminDangerBtn} disabled:cursor-not-allowed disabled:opacity-40`}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </BusyTable>

          <AdminPagination
            className="mt-6"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      </div>

      {showPromote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-black uppercase italic text-gray-900">
              Promote existing user
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isSuperAdmin
                ? "Grant admin or scanner staff access to someone who already has a public-site account. This does not create a new login."
                : "Grant scanner staff access to someone who already has a public-site account. This does not create a new login."}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={promoteForm.email}
                  onChange={(event) => setPromoteForm({ ...promoteForm, email: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="user@echojham.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                {isSuperAdmin ? (
                  <select
                    value={promoteForm.role}
                    onChange={(event) => setPromoteForm({ ...promoteForm, role: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                ) : (
                  <input
                    readOnly
                    value="Staff"
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700"
                  />
                )}
              </div>
              {actionError && <p className="text-sm font-medium text-red-600">{actionError}</p>}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                onClick={closePromote}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePromote}
                disabled={saving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? "Promoting..." : "Promote user"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-black uppercase italic text-gray-900">
              {isStaffAccountRole(editingAdmin.role) ? "Edit Staff" : "Edit Admin"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {isSuperAdmin
                ? "Update this account’s name or access level."
                : "Update this staff account’s name."}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  value={editForm.name}
                  onChange={(event) => setEditForm({ ...editForm, name: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Account name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                {isSuperAdmin ? (
                  <select
                    value={editForm.role}
                    onChange={(event) => setEditForm({ ...editForm, role: event.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                ) : (
                  <input
                    readOnly
                    value="Staff"
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700"
                  />
                )}
              </div>
              {actionError && <p className="text-sm font-medium text-red-600">{actionError}</p>}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                onClick={closeEdit}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={saving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {demotingAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-black uppercase italic text-gray-900">
              {isStaffAccountRole(demotingAdmin.role) ? "Remove Staff" : "Remove Admin"}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Revoke {isStaffAccountRole(demotingAdmin.role) ? "staff" : "admin"} access and set{" "}
              <span className="font-semibold text-gray-900">
                {demotingAdmin.full_name || demotingAdmin.email || "this account"}
              </span>{" "}
              back to customer. They will keep their public-site login.
            </p>
            {actionError && <p className="mt-4 text-sm font-medium text-red-600">{actionError}</p>}

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                onClick={closeDemote}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDemote}
                disabled={saving}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving
                  ? "Removing..."
                  : isStaffAccountRole(demotingAdmin.role)
                    ? "Revoke staff access"
                    : "Revoke admin access"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
